import { yupResolver } from '@hookform/resolvers/yup';
import { InputText } from 'primereact/inputtext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import { EmployeeCollection } from '../../../../../types/api/employees';
import { HubCollection } from '../../../../../types/api/hubs';
import { queryString } from '../../../../../utils/helpers/http';
import { getChangedValues } from '../../../../../utils/helpers/object';
import AutoCompleteInput from '../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../Forms/ReactHookForm/FieldWithErrors';
import { SortingMachinesType } from '../../SortingMachine.functions';
import {
  CreateSortingMachinesType,
  getDefaultValues,
  getValidationSchema,
  toApiData,
} from './CreateDialogSortingMachine.functions';

type Props = {
  isEditDialog: boolean;
  sortingMachineData: SortingMachinesType | undefined;
  handleCreation: (data: CreateSortingMachinesType) => void;
  handleEditing: (data: any) => void;
};

function FormContainer({
  isEditDialog,
  sortingMachineData,
  handleCreation,
  handleEditing,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const [searchEmployee, setSearchEmployee] = useState('');

  const defaultValues = useMemo(
    () => getDefaultValues(isEditDialog, sortingMachineData),
    [isEditDialog, sortingMachineData]
  );

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);

  const methods = useForm<CreateSortingMachinesType>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { handleSubmit, reset, getValues, setValue } = methods;

  const { data: hubs, isLoading: hubsIsLoading } = useAxiosHook<HubCollection>(
    '/hubs' +
      queryString({
        page: 1,
        limit: 0,
      })
  );
  const hubsOptions = useMemo(
    () =>
      hubs?.data.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
    [hubs]
  );

  const { data: employees, isLoading: isEmployeesLoading } =
    useAxiosHook<EmployeeCollection>(`/employees?name=${searchEmployee}`);

  const employeesOptions = useMemo(
    () =>
      (employees?.data ?? []).map((item) => ({
        value: item.id,
        label: item.ime + ' ' + item.prezime,
      })) ?? [],
    [employees]
  );

  const onSearchEmployee = useCallback(
    (e: string) => {
      setSearchEmployee(e);
      setValue('employeeName', e);
    },
    [setSearchEmployee, setValue]
  );

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  function handleFormSubmission(values: any) {
    const params = toApiData(values);
    console.log('Sorting machine payload:', params);

    if (isEditDialog) {
      handleEditing(getChangedValues(params, toApiData(defaultValues)));
      return;
    }

    handleCreation(params);
  }

  return (
    <FormProvider {...methods}>
      <form
        id="sorting-machine-create-edit-form"
        onSubmit={handleSubmit(handleFormSubmission)}
      >
        <div className="p-fluid">
          <FieldWithErrors name="hubId" label={t('Hubs')}>
            <Controller
              name="hubId"
              render={({ field }) => (
                <AutoCompleteInput
                  placeholder={t('Search')}
                  options={hubsOptions}
                  value={field.value}
                  filterValue={getValues('hubName') ?? ''}
                  onFilterChange={(e) => setValue('hubName', e)}
                  onSelectionChange={field.onChange}
                  filterDataCy="hubId"
                  optionsClassName="data-cy-hubId-options"
                  isLoading={hubsIsLoading}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="name" label={t('Name')}>
            <Controller
              name="name"
              render={({ field }) => (
                <InputText
                  id="name"
                  name="name"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="maxPorts" label={t('Max Ports')}>
            <Controller
              name="maxPorts"
              render={({ field }) => (
                <InputText
                  id="maxPorts"
                  name="maxPorts"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="employeeId" label={t('Employee')}>
            <Controller name="employeeId" render={({ field }) => (
              <AutoCompleteInput
                name="employees"
                isLoading={isEmployeesLoading}
                placeholder={t('Search employees')}
                options={employeesOptions}
                filterValue={getValues('employeeName') ?? ''}
                onFilterChange={onSearchEmployee}
                onSelectionChange={(val) => {
                  field.onChange(val);
                  // Set the display name from the selected option
                  const found = employeesOptions.find(e => e.value === val);
                  if (found) setValue('employeeName', found.label);
                }}
                value={field.value}
                filterDataCy="employees"
                optionsClassName="employees-cy-headquarter"
              />
            )} />

          </FieldWithErrors>
        </div>
      </form>
    </FormProvider>
  );
}

export default FormContainer;
